/**
 * CreateThread — Twitter-style thread composer.
 * 2-25 posts, 280 char limit each, media per post.
 * Accepts pre-selected media from Gallery via location.state.
 */

import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { X, Plus, Image, Trash2, Loader2, Lock, Video } from 'lucide-react'
import Avatar from '../components/ui/Avatar'
import MediaPickerModal from '../components/MediaPickerModal'
import type { SelectedMedia } from '../components/MediaPickerModal'
import api from '../services/api'

interface ThreadPost {
  id: string
  text: string
  mediaFile?: File
  mediaPreview?: string
  mediaType?: 'image' | 'video'
  serverMediaUrl?: string // from picker or gallery pre-selection
}

const CHAR_LIMIT = 500
const MAX_POSTS = 25

export default function CreateThread() {
  const navigate = useNavigate()
  const location = useLocation()
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const [posts, setPosts] = useState<ThreadPost[]>([
    { id: '1', text: '' },
    { id: '2', text: '' },
  ])
  const [isPosting, setIsPosting] = useState(false)
  const [mediaPickerFor, setMediaPickerFor] = useState<string | null>(null)

  const userProfile = JSON.parse(localStorage.getItem('user_profile') || '{}')
  const user = {
    name: userProfile.name || 'User',
    username: userProfile.username || 'user',
    avatar: userProfile.avatar,
  }

  // Accept pre-selected media from Gallery multi-select
  useEffect(() => {
    const state = location.state as any
    if (state?.preSelectedMedia && Array.isArray(state.preSelectedMedia)) {
      const newPosts: ThreadPost[] = state.preSelectedMedia.map((m: any, i: number) => ({
        id: `gallery_${i}_${Date.now()}`,
        text: '',
        mediaPreview: m.thumbnail || m.url,
        mediaType: m.type === 'video' ? 'video' as const : 'image' as const,
        serverMediaUrl: m.url,
      }))
      // Ensure minimum 2 posts
      while (newPosts.length < 2) {
        newPosts.push({ id: `empty_${newPosts.length}_${Date.now()}`, text: '' })
      }
      setPosts(newPosts)
    }
  }, [])

  const handlePickerSelect = (media: SelectedMedia | SelectedMedia[]) => {
    if (!mediaPickerFor) return
    const m = Array.isArray(media) ? media[0] : media
    if (m) {
      setPosts(prev => prev.map(p => p.id === mediaPickerFor ? {
        ...p,
        mediaPreview: m.url,
        mediaType: m.type,
        serverMediaUrl: m.url,
      } : p))
    }
    setMediaPickerFor(null)
  }

  const addPost = () => {
    if (posts.length >= MAX_POSTS) return
    setPosts(prev => [...prev, { id: Date.now().toString(), text: '' }])
  }

  const removePost = (id: string) => {
    if (posts.length <= 2) return
    setPosts(prev => prev.filter(p => p.id !== id))
  }

  const updateText = (id: string, text: string) => {
    if (text.length > CHAR_LIMIT) return
    setPosts(prev => prev.map(p => p.id === id ? { ...p, text } : p))
  }

  const handleMediaSelect = (postId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 50 * 1024 * 1024) { alert('File size must be less than 50MB'); return }
    const isImage = file.type.startsWith('image/')
    const isVideo = file.type.startsWith('video/')
    if (!isImage && !isVideo) return

    const reader = new FileReader()
    reader.onload = (event) => {
      setPosts(prev => prev.map(p => p.id === postId ? {
        ...p, mediaFile: file, mediaPreview: event.target?.result as string,
        mediaType: isImage ? 'image' : 'video', serverMediaUrl: undefined,
      } : p))
    }
    reader.readAsDataURL(file)
  }

  const removeMedia = (postId: string) => {
    setPosts(prev => prev.map(p => p.id === postId ? {
      ...p, mediaFile: undefined, mediaPreview: undefined, mediaType: undefined, serverMediaUrl: undefined,
    } : p))
  }

  const handlePost = async () => {
    const validPosts = posts.filter(p => p.text.trim() || p.mediaFile || p.serverMediaUrl)
    if (validPosts.length < 2) { alert('A thread needs at least 2 posts'); return }

    try {
      setIsPosting(true)
      const threadPosts = []
      for (const post of validPosts) {
        let mediaUrl = post.serverMediaUrl
        if (post.mediaFile && !mediaUrl) {
          const uploadResult = await api.uploadMedia(post.mediaFile)
          if (uploadResult.success) mediaUrl = uploadResult.url
        }
        threadPosts.push({
          content: post.text.trim() || undefined,
          media_url: mediaUrl,
          media_type: post.mediaType === 'image' ? 'photo' : post.mediaType,
        })
      }

      const token = localStorage.getItem('auth_token') || localStorage.getItem('token')
      const response = await fetch('https://api.onrecord.live/threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ posts: threadPosts, visibility: 'public' }),
      })

      const data = await response.json()
      if (data.success) navigate('/')
      else alert(data.error || 'Failed to create thread')
    } catch { alert('Failed to create thread') }
    finally { setIsPosting(false) }
  }

  const validCount = posts.filter(p => p.text.trim() || p.mediaFile || p.serverMediaUrl).length
  const canPost = validCount >= 2 && !isPosting

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full" disabled={isPosting}>
            <X className="w-5 h-5 text-gray-700" />
          </button>
          <div className="text-sm text-gray-500">{validCount}/{posts.length} posts</div>
          <button onClick={handlePost} disabled={!canPost}
            className={`px-6 py-2 rounded-full font-semibold transition-all ${
              canPost ? 'bg-onrecord-orange text-white hover:bg-onrecord-orange-dark' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}>
            {isPosting ? <div className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Posting...</div> : 'Post Thread'}
          </button>
        </div>
      </div>

      {/* Thread Posts */}
      <div className="max-w-2xl mx-auto">
        {posts.map((post, index) => (
          <div key={post.id} className="relative">
            {index < posts.length - 1 && (
              <div className="absolute left-[31px] top-14 bottom-0 w-0.5 bg-gray-200" />
            )}
            <div className="flex gap-3 p-4">
              <div className="flex flex-col items-center">
                <Avatar src={user.avatar} alt={user.name} size="md" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm">
                    <span className="font-bold">{user.name}</span>
                    <span className="text-gray-500 ml-1">@{user.username}</span>
                  </div>
                  {posts.length > 2 && (
                    <button onClick={() => removePost(post.id)} className="p-1 text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <textarea value={post.text} onChange={(e) => updateText(post.id, e.target.value)}
                  placeholder={index === 0 ? "Start your thread..." : "Add to your thread..."}
                  className="w-full text-[15px] resize-none outline-none placeholder-gray-400 min-h-[60px]"
                  rows={3} disabled={isPosting} />

                {post.text.length > CHAR_LIMIT - 20 && (
                  <div className={`text-xs text-right ${post.text.length >= CHAR_LIMIT ? 'text-red-500' : 'text-orange-500'}`}>
                    {post.text.length}/{CHAR_LIMIT}
                  </div>
                )}

                {post.mediaPreview && (
                  <div className="relative mt-2 rounded-xl overflow-hidden border border-gray-200">
                    {post.mediaType === 'image'
                      ? <img src={post.mediaPreview} alt="" className="w-full max-h-60 object-cover" />
                      : <video src={post.mediaPreview} className="w-full max-h-60 object-cover" controls />}
                    <button onClick={() => removeMedia(post.id)} className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 rounded-full">
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                )}

                {/* Media buttons — Gallery + Media picker */}
                {!post.mediaPreview && (
                  <div className="mt-2 flex items-center gap-1">
                    <button onClick={() => fileInputRefs.current[post.id]?.click()}
                      className="text-onrecord-orange hover:bg-orange-50 p-2 rounded-full transition-colors" title="Gallery">
                      <Image className="w-5 h-5" />
                    </button>
                    <button onClick={() => setMediaPickerFor(post.id)}
                      className="text-onrecord-orange hover:bg-orange-50 p-2 rounded-full transition-colors" title="Private / Shared media">
                      <Lock className="w-5 h-5" />
                    </button>
                    <input ref={(el) => { fileInputRefs.current[post.id] = el }} type="file" accept="image/*,video/*"
                      onChange={(e) => handleMediaSelect(post.id, e)} className="hidden" />
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {posts.length < MAX_POSTS && (
          <div className="flex items-center gap-3 px-4 py-3 border-t border-gray-100">
            <div className="w-10 flex justify-center"><div className="w-0.5 h-4 bg-gray-200" /></div>
            <button onClick={addPost}
              className="flex items-center gap-2 text-onrecord-orange hover:bg-orange-50 px-4 py-2 rounded-full transition-colors text-sm font-medium">
              <Plus className="w-4 h-4" />Add to thread
            </button>
            <span className="text-xs text-gray-400">{posts.length}/{MAX_POSTS}</span>
          </div>
        )}
      </div>

      {/* Tabbed Media Picker */}
      <MediaPickerModal
        isOpen={!!mediaPickerFor}
        onClose={() => setMediaPickerFor(null)}
        onSelect={handlePickerSelect}
      />
    </div>
  )
}
